import { hasValidResponse, QuestionRefWithId, ResponseValue } from "./types";

/**
 * Determine if a question should be visible in test mode.
 * In test mode, only show:
 * - All answered questions
 * - The first unanswered question (so users have something to fill in)
 *
 * @param questionId - The current question's ID
 * @param testMode - Whether test mode is enabled
 * @param allQuestions - Ordered list of all questions with their visibility rules
 * @param responses - Current response values
 * @param evaluatePredicate - Function to evaluate FEEL predicates
 * @returns true if question should be shown, false if hidden
 */
export const shouldShowQuestionInTestMode = (
  questionId: number,
  testMode: boolean,
  allQuestions: QuestionRefWithId[],
  responses: Record<number, ResponseValue>,
  evaluatePredicate: (predicate: string) => boolean
): boolean => {
  // If test mode is off or no questions list, show everything
  if (!testMode || allQuestions.length === 0) {
    return true;
  }

  const currentQuestionAnswered = hasValidResponse(responses[questionId]);

  // Always show answered questions
  if (currentQuestionAnswered) {
    return true;
  }

  // This question is unanswered - check if there's an earlier unanswered question
  const currentIndex = allQuestions.findIndex(q => q.questionId === questionId);

  // If not in list yet (still loading), show it
  if (currentIndex === -1) {
    return true;
  }

  // Check all questions BEFORE this one
  for (let i = 0; i < currentIndex; i++) {
    const prevQRef = allQuestions[i];

    // Check if previous question is visible
    const prevVisible =
      prevQRef.visibility === "visible"
        ? true
        : evaluatePredicate((prevQRef.visibility as { visibleWhen: string }).visibleWhen);

    if (prevVisible) {
      const prevAnswered = hasValidResponse(responses[prevQRef.questionId]);

      if (!prevAnswered) {
        // Found an earlier unanswered visible question - hide this one
        return false;
      }
    }
  }

  // No earlier unanswered question - this is the first, show it
  return true;
};
